import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import i18n from '@/utils/i18n';
import React, { useCallback, useState } from 'react';
import { AccountDetailItemsOfInterestSkeleton } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/components/account-detail-items-of-interest-skeleton';
import { Chip, Icons, Typography } from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_EASING_IN_OUT_CONFIG } from '@experiences/blocs/utils/animations';
import { DetailSectionEmptyState } from '@/experiences/sales-portal/components/detail-section-empty-state';
import { ItemImage } from '@usepepper/ui/atoms/item-image';
import { ItemOfInterest } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/blocs/loaders/use-account-items-of-interest-loader';
import { LayoutChangeEvent, View } from 'react-native';
import { Spacing } from '@usepepper/sprout-kit';
import { useIsMobileBottomNavLayout } from '@sales-portal/blocs/hooks/use-is-mobile-bottom-nav-layout';

interface Props {
  items: ItemOfInterest[] | null;
}

const COLLAPSED_COUNT = 6;
const COLUMNS = 2;

// Minimum readable width before wrapping to single column
export const ITEM_CARD_MIN_WIDTH = 200;

export const AccountDetailItemsOfInterest: React.FC<Props> = ({ items }) => {
  const [styles] = useStyles();
  const isMobileBottomNavLayout = useIsMobileBottomNavLayout();

  const [isExpanded, setIsExpanded] = useState(false);
  const [overflowHeight, setOverflowHeight] = useState(0);
  const animatedHeight = useSharedValue(0);

  const resolvedItems = items ?? [];
  const hasItems = resolvedItems.length > 0;
  const showViewMore = resolvedItems.length > COLLAPSED_COUNT;

  const visibleItems = resolvedItems.slice(0, COLLAPSED_COUNT);
  const overflowItems = resolvedItems.slice(COLLAPSED_COUNT);

  const clipStyle = useAnimatedStyle(() => ({
    height: animatedHeight.value,
    overflow: 'hidden',
  }));

  const handleMeasureOverflow = useCallback((event: LayoutChangeEvent) => {
    setOverflowHeight(event.nativeEvent.layout.height);
  }, []);

  const handleViewMore = useCallback(() => {
    animatedHeight.value = withTiming(
      isExpanded ? 0 : overflowHeight,
      DEFAULT_EASING_IN_OUT_CONFIG,
    );
    setIsExpanded((prev) => !prev);
  }, [animatedHeight, isExpanded, overflowHeight]);

  return (
    <View>
      <View
        style={[
          styles.header,
          isMobileBottomNavLayout ? null : styles.headerDesktop,
        ]}
      >
        <Typography
          accessibilityRole="header"
          bold={!isMobileBottomNavLayout}
          size={isMobileBottomNavLayout ? '$lg' : '$md'}
          variant="heading"
        >
          {i18n.t('sales_portal.account_detail.items_of_interest.title')}
        </Typography>
        {hasItems ? (
          <Typography color="$textTertiary" size="$xs">
            {i18n.t('sales_portal.account_detail.items_of_interest.past_days')}
          </Typography>
        ) : null}
      </View>

      {items === null ? (
        <AccountDetailItemsOfInterestSkeleton />
      ) : hasItems ? null : (
        <DetailSectionEmptyState
          text={i18n.t(
            'sales_portal.account_detail.items_of_interest.empty_state',
          )}
        />
      )}

      {hasItems ? (
        <View style={styles.itemsGrid}>
          <ItemRows items={visibleItems} />
        </View>
      ) : null}

      {showViewMore ? (
        <>
          <Animated.View style={clipStyle}>
            <View style={styles.overflowGrid}>
              <ItemRows items={overflowItems} />
            </View>
          </Animated.View>

          <View
            onLayout={handleMeasureOverflow}
            pointerEvents="none"
            style={styles.overflowMeasure}
          >
            <View style={styles.overflowGrid}>
              <ItemRows items={overflowItems} />
            </View>
          </View>

          <View style={styles.viewMoreContainer}>
            <Chip
              alignSelf="center"
              circular
              onPress={handleViewMore}
              pressable
              size="$md"
            >
              <Chip.Text>
                {isExpanded
                  ? i18n.t(
                      'sales_portal.account_detail.items_of_interest.view_less',
                    )
                  : i18n.t(
                      'sales_portal.account_detail.items_of_interest.view_more',
                    )}
              </Chip.Text>
              <Chip.Icon>
                {isExpanded ? <Icons.ChevronUp /> : <Icons.ChevronDown />}
              </Chip.Icon>
            </Chip>
          </View>
        </>
      ) : null}
    </View>
  );
};

const ItemCard: React.FC<{ item: ItemOfInterest }> = ({ item }) => {
  const [styles] = useStyles();

  return (
    <View style={styles.itemCard}>
      <ItemImage
        alt={item.name}
        imageURLs={item.imageUrls}
        size={Spacing.SPACE_2XLARGE}
      />
      <View style={styles.itemDetails}>
        <View style={styles.itemDetailRow}>
          <View style={styles.itemTitleGroup}>
            <Typography bold numberOfLines={1} size="$sm">
              {item.name}
            </Typography>
            <Typography color="$textTertiary" numberOfLines={1} size="$xs">
              {item.interactionText}
            </Typography>
          </View>
          <Typography color="$textTertiary" size="$xs">
            {item.dateLabel}
          </Typography>
        </View>
      </View>
    </View>
  );
};

const ItemRows: React.FC<{ items: ItemOfInterest[] }> = ({ items }) => {
  const [styles] = useStyles();
  const rows: ItemOfInterest[][] = [];

  // Group items into rows of COLUMNS (e.g. [[item0, item1], [item2, item3], [item4]])
  for (let i = 0; i < items.length; i += COLUMNS) {
    rows.push(items.slice(i, i + COLUMNS));
  }

  return (
    <>
      {rows.map((row) => (
        <View key={row[0].interactionId} style={styles.itemRow}>
          {row.map((item) => (
            <View key={item.interactionId} style={styles.itemCell}>
              <ItemCard item={item} />
            </View>
          ))}
          {/* Spacer cell to preserve column layout when the last row is incomplete */}
          {row.length < COLUMNS ? <View style={styles.itemCell} /> : null}
        </View>
      ))}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  header: {
    paddingBottom: theme.sk.spacing.spaceSMedium,
  },
  headerDesktop: {
    paddingTop: theme.sk.spacing.spaceSMedium,
  },
  itemCard: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSMedium,
  },
  itemCell: {
    flex: 1,
    minWidth: ITEM_CARD_MIN_WIDTH,
  },
  itemDetailRow: {
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceMedium,
    justifyContent: 'space-between',
  },
  itemDetails: {
    flex: 1,
  },
  itemRow: {
    columnGap: theme.sk.spacing.space3xlarge,
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: theme.sk.spacing.spaceMedium,
  },
  itemsGrid: {
    rowGap: theme.sk.spacing.spaceMedium,
  },
  itemTitleGroup: {
    flex: 1,
  },
  overflowGrid: {
    paddingTop: theme.sk.spacing.spaceMedium,
    rowGap: theme.sk.spacing.spaceMedium,
  },
  overflowMeasure: {
    left: 0,
    opacity: 0,
    position: 'absolute',
    right: 0,
    zIndex: -1,
  },
  viewMoreContainer: {
    alignItems: 'center',
    paddingTop: theme.sk.spacing.spaceMedium,
  },
}));
